/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { buildSearchEngine, SearchEngine } from '@coveo/headless';
import { PlatformRequestOptions } from '@coveo/headless/dist/definitions/api/preprocess-request';

interface EngineProviderProps {
  config: {
    searchHub: string;
    pipeline: string;
    accessToken: string;
    organizationId: string;
  };
  children: React.ReactNode;
  userCoordinates?: {
    latitude: number;
    longitude: number;
  };
  useDistanceSorting?: boolean;
  fieldsToInclude?: string[];
}

const EngineContext = createContext<SearchEngine | null>(null);
export const useCoveoEngine = () => useContext(EngineContext);

type EngineControls = {
  setDistanceOverride: (override: 'on' | 'off' | null) => void;
};

const EngineControlsContext = createContext<EngineControls | null>(null);
export const useCoveoEngineControls = () => useContext(EngineControlsContext);

export const CoveoEngineProvider = ({
  config,
  children,
  userCoordinates,
  useDistanceSorting = false, // default false
  fieldsToInclude = [],
}: EngineProviderProps) => {
  const [engine, setEngine] = useState<SearchEngine | null>(null);
  const userCoordinatesRef = useRef<{ latitude: number; longitude: number } | null>(
    userCoordinates ?? null
  );
  const useDistanceSortingRef = useRef<boolean>(useDistanceSorting);
  const fieldsToIncludeRef = useRef<string[]>(fieldsToInclude);
  const distanceOverrideRef = useRef<'on' | 'off' | null>(null);

  useEffect(() => {
    const configuration: Parameters<typeof buildSearchEngine>[0]['configuration'] = {
      accessToken: config.accessToken,
      organizationId: config.organizationId,
      search: {
        pipeline: config.pipeline,
        searchHub: config.searchHub,
      },
      analytics: {
        analyticsMode: 'legacy',
      },
    };

    configuration.preprocessRequest = (
      request: PlatformRequestOptions,
      clientOrigin: string,
      metadata?: { [key: string]: any }
    ): PlatformRequestOptions => {
      if (
        clientOrigin === 'searchApiFetch' &&
        metadata?.method === 'search' &&
        typeof request.body === 'string'
      ) {
        try {
          const body = JSON.parse(request.body);

          // Always merge fieldsToInclude ref into the request
          const dynamicFields = Array.from(
            new Set([...(body.fieldsToInclude || []), ...(fieldsToIncludeRef.current || [])])
          );
          body.fieldsToInclude = dynamicFields;

          // Decide whether to enable distance features for this request
          const hasCoords = !!userCoordinatesRef.current;
          let enableDistance = false;
          if (distanceOverrideRef.current === 'off') {
            enableDistance = false;
          } else if (distanceOverrideRef.current === 'on') {
            enableDistance = hasCoords;
          } else {
            // default auto mode: enable when we have coordinates
            enableDistance = hasCoords;
          }

          // Conditionally add distance query functions based on decision
          if (userCoordinatesRef.current) {
            const { latitude, longitude } = userCoordinatesRef.current;
            body.queryFunctions = [
              {
                fieldName: '@distance',
                function: `dist(@cwlatitude, @cwlongitude, ${latitude}, ${longitude})`,
              },
              {
                fieldName: '@distanceinmiles',
                function: '@distance*0.000621371',
              },
            ];

            // Ensure distance fields are included when sorting by distance
            body.fieldsToInclude = Array.from(
              new Set([
                ...(body.fieldsToInclude || []),
                'distanceinmiles',
                'cwlatitude',
                'cwlongitude',
              ])
            );

            // Ensure the request uses distance sort when coordinates exist
            if (enableDistance && body.sortCriteria !== 'field:distanceinmiles ascending') {
              body.sortCriteria = 'distanceinmiles ascending';
            }
          }

          // If distance mode is not enabled (no coords or explicitly off),
          // ensure we are not sorting by distance and fall back to relevancy.
          if (!userCoordinatesRef.current || !enableDistance) {
            const sortCriteria = (body.sortCriteria || '').toString().toLowerCase();
            if (sortCriteria.includes('distance')) {
              body.sortCriteria = 'relevancy';
            }
          }

          request.body = JSON.stringify(body);
        } catch (err) {
          console.error('Error applying distance query functions:', err);
        }
      }
      return request;
    };

    const engineInstance = buildSearchEngine({ configuration });
    setEngine(engineInstance);
  }, [config]);

  // Keep refs in sync with props without recreating the engine
  useEffect(() => {
    userCoordinatesRef.current = userCoordinates ?? null;
  }, [userCoordinates]);

  useEffect(() => {
    // Mirror prop into ref (override can still disable/enable)
    useDistanceSortingRef.current = !!useDistanceSorting;
  }, [useDistanceSorting]);

  useEffect(() => {
    fieldsToIncludeRef.current = fieldsToInclude ?? [];
  }, [fieldsToInclude]);

  const controls = useMemo<EngineControls>(
    () => ({
      setDistanceOverride: (override) => {
        distanceOverrideRef.current = override;
      },
    }),
    []
  );

  if (!engine) return null;
  return (
    <EngineContext.Provider value={engine}>
      <EngineControlsContext.Provider value={controls}>{children}</EngineControlsContext.Provider>
    </EngineContext.Provider>
  );
};
